/**
 * Sentry クライアントのノイズ除外パターン（#864）。
 *
 * アプリのバグではない「ブラウザ/ネットワーク環境起因」のイベントが error として
 * 大量に届き、本当に対応が必要なエラーが埋もれるのを防ぐ。
 *
 * 除外して安全な理由:
 * - "Connection to Indexed Database server lost" は Safari 固有のブラウザ都合で
 *   コード側では修正不能（SHIFTSMART-2DB: 353件/93ユーザ）。
 * - "Load failed" / "Failed to fetch" 系は fetch が【レスポンスを一切受け取れなかった】
 *   純粋な通信断のときの TypeError メッセージ。サーバが 5xx を返した場合は
 *   レスポンスが存在するため別メッセージ（"Response not successful: Received status
 *   code 502" 等）になり、このリストには一致しない＝5xx 起因は今後も Sentry に残る。
 *   通信断は RetryLink（3回リトライ, #727）導入済みで、残るのは実際のオフラインのみ。
 */
export const sentryIgnoreErrors: (string | RegExp)[] = [
  // Safari の IndexedDB 接続断（ブラウザ都合・修正不能）
  "Connection to Indexed Database server lost",
  // 純粋なネットワーク断による fetch 失敗（ブラウザ別のメッセージ差分を網羅）。
  // 必ず末尾をアンカーする: 部分一致にすると "Failed to fetch dynamically
  // imported module"（追跡したいキャッシュ問題, _app.tsx beforeSend 参照）まで
  // 誤って握りつぶすため。Sentry は message / "Type: value" / value に対して
  // regex.test で照合するので、エラー型プレフィックスは任意一致にする。
  /^(?:\w+Error: )?Load failed$/, // Safari
  /^(?:\w+Error: )?Failed to fetch$/, // Chrome
  /^(?:\w+Error: )?Network request failed$/,
  "NetworkError when attempting to fetch resource", // Firefox
  "The network connection was lost", // iOS WebKit
  // セッション切れ(トークン期限切れ)由来の UNAUTHORIZED (SHIFTSMART-2P6)。
  // interface/utils/client.ts の errorLink が「トークン強制リフレッシュ→1回再試行→
  // それでも駄目ならログインへ誘導」で既にハンドリング済み＝ユーザー影響は復帰済みの
  // 期待挙動で、アプリのバグではない。値が厳密に "UNAUTHORIZED" のものだけを対象に
  // アンカーし、"You are UNAUTHORIZED to ..." のような本物の認可エラーは残す。
  /^(?:\w+Error: )?UNAUTHORIZED$/,
]

/**
 * Sentry の ignoreErrors と同じ意味論（string は部分一致 / RegExp は test）で
 * メッセージが除外対象かを判定する。ユニットテストからパターンの過不足を
 * 検証するためのヘルパー。
 */
export function isIgnoredErrorMessage(message: string): boolean {
  return sentryIgnoreErrors.some((pattern) =>
    typeof pattern === "string" ? message.includes(pattern) : pattern.test(message),
  )
}

/** beforeSend が受け取るイベントのうち、この判定に必要な部分だけの構造型 */
type EventLike = {
  exception?: {
    values?: Array<{
      stacktrace?: {
        frames?: Array<{ filename?: string | null }>
      }
    }>
  }
}

/**
 * ブラウザが注入したスクリプト由来で、発生箇所を一切特定できないイベントか (#862)。
 *
 * Chrome for iOS はページに自前のスクリプト（自動入力・フォーム走査など）を注入する。
 * そこで起きた例外は window.onerror に source が失われた状態で届き、Sentry には
 * 全フレームの filename が "undefined" / "<anonymous>" のイベントとして記録される。
 * SHIFTSMART-353（RangeError: Maximum call stack size exceeded, 347件）は
 * 347件すべてが Chrome Mobile iOS で、この形だった（Safari では 0 件 =
 * アプリのコードなら同じ WebKit の Safari でも起きるはずなので、注入スクリプト起因）。
 *
 * 落として安全な理由:
 * - アプリ由来のエラーは必ず配信元 (/_next/ 等) の filename を持つフレームを含む
 * - フレームが 1 つも無いイベント（メッセージのみ）はこの判定では落とさない
 * - 全フレームが出所不明のイベントはソースマップも適用できず、対応不能なノイズ
 */
export function isUnattributableThirdPartyEvent(event: EventLike): boolean {
  const values = event.exception?.values ?? []
  const frames = values.flatMap((value) => value.stacktrace?.frames ?? [])
  if (frames.length === 0) {
    return false
  }
  return frames.every((frame) => {
    const filename = frame.filename
    return (
      filename == null || filename === "undefined" || filename === "<anonymous>"
    )
  })
}
